/**
 * Bill of Lading Charges Fields Component
 *
 * Two sections:
 * 1. charges[] — dynamic array rendered as a table (same style as OBChargesFields),
 *    with additional fields: chargeType, consignmentItemIndex, equipmentReference
 * 2. totalCharges — static form group for prepaid/collect totals with currencies
 *
 * Payment term codes follow DCSA: PRE = Prepaid, COL = Collect.
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

const PAYMENT_TERM_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'PRE', label: 'PRE - Prepaid' },
  { value: 'COL', label: 'COL - Collect' }
];

const CHARGE_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'FREIGHT', label: 'Freight' },
  { value: 'ORIGIN', label: 'Origin' },
  { value: 'DESTINATION', label: 'Destination' },
  { value: 'INLAND', label: 'Inland' },
  { value: 'SURCHARGE', label: 'Surcharge' },
  { value: 'OTHER', label: 'Other' }
];

/**
 * BLChargesFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLChargesFields({ control, register, watch, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'charges'
  });

  const addCharge = () => {
    append({
      chargeName: '',
      chargeType: '',
      currencyAmount: null,
      currencyCode: 'USD',
      paymentTermCode: '',
      calculationBasis: '',
      quantity: null,
      unitPrice: null,
      consignmentItemIndex: null,
      equipmentReference: ''
    });
  };

  return (
    <div className="space-y-6">
      {/* ---- Section 1: Charges list ---- */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="text-lg font-medium text-gray-900">Charges</h3>
          {isEditMode && (
            <Button variant="secondary" size="sm" onClick={addCharge}>
              + Add Charge
            </Button>
          )}
        </div>

        {/* Empty state */}
        {fields.length === 0 && (
          <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
            No charges listed.
          </div>
        )}

        {/* Charges table */}
        {fields.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Charge Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Term</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Calc Basis</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Consignment Idx</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Equipment Ref</th>
                  {isEditMode && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map((field, index) => (
                  <tr key={field.id}>
                    <td className="px-3 py-2">
                      <input
                        {...register(`charges.${index}.chargeName`)}
                        disabled={!isEditMode}
                        className="w-28 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                        placeholder="Charge name"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        {...register(`charges.${index}.chargeType`)}
                        disabled={!isEditMode}
                        className="w-28 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      >
                        {CHARGE_TYPE_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        {...register(`charges.${index}.currencyAmount`, { valueAsNumber: true })}
                        disabled={!isEditMode}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`charges.${index}.currencyCode`)}
                        disabled={!isEditMode}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                        placeholder="USD"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        {...register(`charges.${index}.paymentTermCode`)}
                        disabled={!isEditMode}
                        className="w-28 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      >
                        {PAYMENT_TERM_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`charges.${index}.calculationBasis`)}
                        disabled={!isEditMode}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                        placeholder="Per container"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        {...register(`charges.${index}.quantity`, { valueAsNumber: true })}
                        disabled={!isEditMode}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        {...register(`charges.${index}.unitPrice`, { valueAsNumber: true })}
                        disabled={!isEditMode}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        {...register(`charges.${index}.consignmentItemIndex`, { valueAsNumber: true })}
                        disabled={!isEditMode}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        {...register(`charges.${index}.equipmentReference`)}
                        disabled={!isEditMode}
                        className="w-28 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                        placeholder="Container no."
                      />
                    </td>
                    {isEditMode && (
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---- Section 2: Total Charges ---- */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
          Total Charges
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            label="Prepaid Total"
            type="number"
            step="0.01"
            {...register('totalCharges.prepaidTotal', { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'totalCharges.prepaidTotal')}
          />

          <Input
            label="Prepaid Currency"
            {...register('totalCharges.prepaidCurrency')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'totalCharges.prepaidCurrency')}
            placeholder="e.g., USD"
          />

          <Input
            label="Collect Total"
            type="number"
            step="0.01"
            {...register('totalCharges.collectTotal', { valueAsNumber: true })}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'totalCharges.collectTotal')}
          />

          <Input
            label="Collect Currency"
            {...register('totalCharges.collectCurrency')}
            disabled={!isEditMode}
            issues={getFieldIssues(issues, 'totalCharges.collectCurrency')}
            placeholder="e.g., USD"
          />
        </div>
      </div>
    </div>
  );
}

export default BLChargesFields;
